import { ChakraStylesConfig, GroupBase } from 'chakra-react-select';

import { i18nType } from '@oaknorth/util-intl';

import { InterpolatableGroupBase } from '../types';

export const getOptionsAreGrouped = <OptionType>(
  options: OptionType[] | InterpolatableGroupBase<OptionType>[] | GroupBase<OptionType>[]
): options is GroupBase<OptionType>[] | InterpolatableGroupBase<OptionType>[] => {
  return options.length > 0 && !!options[0] && typeof options[0] === 'object' && 'options' in options[0];
};

export const normaliseGroups = <OptionType>(
  groups: InterpolatableGroupBase<OptionType>[],
  i18n: i18nType
): GroupBase<OptionType>[] => {
  return groups.map((group) => ({
    ...group,
    label: group.label ? i18n.stringify(group.label) : undefined,
  }));
};

/**
 * General case for a string enum
 */
type StringEnum<T> = { [key: string]: T };

/**
 * Utility function to decide if a string {value} is actually a value from an enum {enumType}
 *
 * Acts as a TS type guard, so once this has been called TS will know that {value} is of the enum type.
 *
 * Only supports string enums
 */
export function isEnumValue<T extends string>(
  enumType: StringEnum<T>,
  value: string | number | null | undefined
): value is T {
  return Object.values(enumType).includes(value as T);
}

/**
 * Sets the default chakraStyles for the select taking into account custom styles passed in
 * for a particular select. These styles allow the select to size itself according to the
 * width of the content for the selected value(s).
 *
 * Without these, the component depends on the size of the container, which may not include space for the current value
 *
 * Overrides chakra-react-select default ValueContainer styles alignItems field and
 * SingleValue styles maxWidth and position fields
 *
 * Changes the default control border colour and radius
 *
 * Overrides these ONCI defaults if the Select instance gets a custom values for them
 */
export const getChakraStylesWithDefaults = <OptionType, IsMultiSelect extends boolean>(
  chakraStyles: ChakraStylesConfig<OptionType, IsMultiSelect, GroupBase<OptionType>> | undefined,
  isMulti: boolean
): ChakraStylesConfig<OptionType, IsMultiSelect, GroupBase<OptionType>> => {
  return {
    ...chakraStyles,
    control: (provided, state) => {
      const customControlStyles = chakraStyles?.control?.(provided, state) as Record<string, unknown> | undefined;
      return {
        ...provided,
        '&:hover': {
          ...(customControlStyles?._hover as object),
          cursor: state.selectProps.isSearchable ? 'cursor' : 'pointer',
        },
        // '&[data-focus-visible="true"]': customControlStyles?._focusVisible,
        borderColor: state.hasValue ? 'link' : 'gray.400',
        // Keep the placeholder/value and dropdown icon on the same row
        flexWrap: 'nowrap',
        ...customControlStyles,
      };
    },
    menuList: (provided, state) => {
      const customMenuListStyles = chakraStyles?.menuList?.(provided, state);

      return {
        ...provided,
        minWidth: 'unset',
        gap: 1,
        padding: 2,
        boxShadow: 'xs',
        ...customMenuListStyles,
      };
    },
    valueContainer: (provided, state) => {
      const customValueContanerStyles = chakraStyles?.valueContainer?.(provided, state);

      const isMultiWithVisibleValue = isMulti && state.hasValue && state.selectProps.controlShouldRenderValue;
      return {
        ...provided,
        // If we're showing multi values (tags), display them centred and wrap if required
        alignItems: isMultiWithVisibleValue ? 'center' : 'flex-start',
        flexWrap: isMultiWithVisibleValue ? 'wrap' : 'nowrap',
        // Dummy input should not force the values to wrap unnecessarily - remove from layout flow
        // Dummy input cannot be styled directly
        'input[inputmode="none"]': {
          position: 'absolute',
        },
        paddingRight: 0,
        whiteSpace: 'nowrap',
        // Ensure we don't try to shrink the placeholder/value
        // flex: '1 0 auto',
        ...customValueContanerStyles,
      };
    },
    singleValue: (provided, state) => {
      const customSingleValueStyles = chakraStyles?.singleValue?.(provided, state);
      return {
        ...provided,
        maxWidth: 'unset',
        position: 'relative',
        // singleValue is in a grid, in the same area as inputContainer
        // inputContainer is taller, so make sure that the placeholder is centred in it
        alignSelf: 'center',
        ...customSingleValueStyles,
      };
    },
    placeholder: (provided, state) => {
      const customPlaceholderStyles = chakraStyles?.placeholder?.(provided, state);
      return {
        ...provided,
        maxWidth: 'unset',
        position: 'relative',
        // If we have a value but we're going to render the placeholder anyway, make it look like a control
        // Otherwise, placeholder is just a placeholder - render like a placeholder
        color: !state.selectProps.controlShouldRenderValue ? 'chakra-body-text' : 'chakra-placeholder-color',
        marginX: 0,
        // Placeholder is in a grid, in the same area as inputContainer
        // inputContainer is taller, so make sure that the placeholder is centred in it
        alignSelf: 'center',
        ...customPlaceholderStyles,
      };
    },
    option: (provided, state) => {
      const customOptionStyles = chakraStyles?.option?.(provided, state);
      return {
        ...provided,
        color: 'gray.900',
        borderRadius: 'md',
        fontSize: 'md',
        _selected: {
          background: 'blue.100',
        },
        _hover: {
          background: 'blue.50',
        },
        _highlighted: {
          background: 'blue.50',
        },
        ...customOptionStyles,
      };
    },
    inputContainer: (provided, state) => {
      return {
        ...provided,
        alignSelf: 'center',
        ...chakraStyles?.inputContainer?.(provided, state),
      };
    },
    dropdownIndicator: (provided, state) => {
      return {
        ...provided,
        width: 'unset',
        ...chakraStyles?.dropdownIndicator?.(provided, state),
      };
    },
  };
};
